import { ComponentType, ReactNode } from 'react'
import { Outlet } from 'react-router-dom'

import i18n from 'modules/core/lib/i18n'

import { UpsellChart } from 'modules/finance/components/FinancialWorkspace/components/chart/UpsellChart'
import { AvailabilitiesProvider } from 'modules/finance/components/FinancialWorkspaceEmployeeCosts/Providers/Availabilities'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import { payrollConceptCategoriesOptions } from 'modules/payroll_bundle/lib/constants'

import { FinancialWorkspace } from '../FinancialWorkspace'
import { BlurTable } from '../FinancialWorkspace/components/Table/BlurTable'
import { InsightTableGroupBy, InsightTableType } from '../FinancialWorkspace/types/table'
import { CustomFiltersProvider } from '../FinancialWorkspaceEmployeeCosts/Providers/CustomFilters'
import { PermissionsProvider } from '../FinancialWorkspaceEmployeeCosts/Providers/PermissionsProvider'
import { useFilters } from '../FinancialWorkspacePayroll/components/Filters/useFilters'
import { PayrollChart } from '../FinancialWorkspacePayroll/components/chart'
import { CompensationsBanner } from './components/CompensationsBanner'
import { Header } from './components/Header'
import { useTableProps } from './components/Table/props'

const FinancialWorkspaceCompensations = ({
  authorized,
  seePayrollLanding,
  canSeeFinanceWorkspaceCompensations,
}: {
  authorized: boolean
  seePayrollLanding: boolean
  canSeeFinanceWorkspaceCompensations: boolean
}) => {
  const tableProps = useTableProps()
  const showUpsell = !canSeeFinanceWorkspaceCompensations || seePayrollLanding

  const CustomProvider: ComponentType<{ children: ReactNode }> = ({ children }) => (
    <PermissionsProvider>
      <AvailabilitiesProvider>
        <CustomFiltersProvider>{children}</CustomFiltersProvider>
      </AvailabilitiesProvider>
    </PermissionsProvider>
  )

  return (
    <ResponsivePage.Body authorized={authorized} showHeader={false} variant="full">
      <FinancialWorkspace
        tableProps={tableProps}
        chart={
          showUpsell ? (
            <UpsellChart
              cta={<CompensationsBanner />}
              title={i18n.t('finance.financial_workspace.payroll.total_company_costs')}
            />
          ) : (
            <PayrollChart />
          )
        }
        dataProvider={CustomProvider}
        additionalFilters={useFilters}
        insightTableType={InsightTableType.Compensation}
        defaultGroupBy={InsightTableGroupBy.LineItem}
        header={!showUpsell && <Header />}
        blurTable={
          showUpsell && (
            <BlurTable
              items={payrollConceptCategoriesOptions().map((option) => option.label)}
              rowChildren={<CompensationsBanner />}
            />
          )
        }
      />

      <Outlet context={{ canSeeFinanceWorkspace: authorized }} />
    </ResponsivePage.Body>
  )
}

export default FinancialWorkspaceCompensations
